import { useState } from 'react';
import { useNavigateWithParams } from '../hooks/useNavigateWithParams';
import { motion } from 'framer-motion';
import { ArrowLeft, Send, CheckCircle2, AlertCircle } from 'lucide-react';
import { useForm, FormProvider } from 'react-hook-form';
import { WEBHOOK_URL, FEATURES, STORAGE_KEYS } from '../config';
import { questions } from '../data/questions';
import type { QuestionnaireData } from '../types';
import { DEFAULT_FORM_DATA } from '../types';
import type { WebhookResponse } from '../types/api';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import {
  ReviewSection,
  ReviewField,
  ReviewList,
  ReviewObjection,
} from '../components/ReviewSection';
import { useFormPersistence } from '../hooks/useFormPersistence';
import { formatTimestamp } from '../lib/utils';
import { getScopedClient } from '../lib/supabase';
import { useTenant } from '../context/TenantContext';

export function Review() {
  const navigate = useNavigateWithParams();
  const { locationId } = useTenant();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const form = useForm<QuestionnaireData>({
    defaultValues: DEFAULT_FORM_DATA,
  });

  const { clearSavedData } = useFormPersistence({ form });

  const formData = form.watch();

  const handleEdit = (questionNumber: number) => {
    navigate(`/question/${questionNumber}`);
  };

  function validateSubmission(data: QuestionnaireData): string[] {
    const errors: string[] = [];

    if (!data.question1?.mistake || data.question1.mistake.length < 10) {
      errors.push('Question 1: Please describe the mistake in more detail (at least 10 characters)');
    }
    if (!data.question1?.fix || data.question1.fix.length < 10) {
      errors.push('Question 1: Please describe the fix in more detail (at least 10 characters)');
    }

    if (!data.question2?.vision || data.question2.vision.length < 20) {
      errors.push('Question 2: Please describe the vision in more detail (at least 20 characters)');
    }

    if (data.question3?.objections) {
      data.question3.objections.forEach((obj, idx) => {
        if (!obj.politeReason || obj.politeReason.length < 5) {
          errors.push(`Question 3, Objection ${idx + 1}: Polite reason is too short (at least 5 characters)`);
        }
        if (!obj.realReason || obj.realReason.length < 5) {
          errors.push(`Question 3, Objection ${idx + 1}: Real reason is too short (at least 5 characters)`);
        }
      });
    } else {
      errors.push('Question 3: Please provide all three objections');
    }

    if (!data.question4?.solution || data.question4.solution.length < 10) {
      errors.push('Question 4: Please describe your solution in more detail (at least 10 characters)');
    }
    if (!data.question4?.originStory || data.question4.originStory.length < 20) {
      errors.push('Question 4: Please share your origin story in more detail (at least 20 characters)');
    }

    if (data.question5?.resources) {
      data.question5.resources.forEach((resource, idx) => {
        if (!resource || resource.trim().length === 0) {
          errors.push(`Question 5: Resource ${idx + 1} is empty`);
        }
      });
    } else {
      errors.push('Question 5: Please provide all three resources');
    }

    if (!data.question6?.scarcity || data.question6.scarcity.length < 10) {
      errors.push('Question 6: Please describe your scarcity element in more detail (at least 10 characters)');
    }

    return errors;
  }

  const handleSubmit = async () => {
    // Validate before submitting
    setValidationErrors([]);
    const errors = validateSubmission(formData);
    if (errors.length > 0) {
      setValidationErrors(errors);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    // Add submission timestamp
    const submissionData: QuestionnaireData = {
      ...formData,
      submittedAt: formatTimestamp(),
    };

    if (FEATURES.webhookSubmission) {
      // Generate a jobId upfront so both the app and Make.com share the same ID,
      // even if Make.com returns a plain-text acknowledgement instead of JSON.
      const clientJobId = crypto.randomUUID();

      try {
        // Create Supabase record BEFORE sending to webhook so Make.com can update it
        try {
          const { error: supabaseError } = await getScopedClient()
            .from('webinars')
            .insert({
              job_id: clientJobId,
              location_id: locationId,
              status: 'processing',
              questionnaire_data: submissionData,
            });

          if (supabaseError) {
            console.warn('Supabase insert failed:', supabaseError.message);
          }
        } catch (supabaseErr) {
          console.warn('Supabase insert error:', supabaseErr);
        }

        const response = await fetch(WEBHOOK_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...submissionData, jobId: clientJobId }),
        });
        if (!response.ok) throw new Error('Submission failed');

        // Make.com may return JSON or a plain-text "Accepted" acknowledgement.
        let jobId: string = clientJobId;
        const responseText = await response.text();
        try {
          const data: WebhookResponse = JSON.parse(responseText);
          if (data.success === false) {
            throw new Error('The server returned an unsuccessful response. Please try again.');
          }
          if (data.jobId) {
            jobId = data.jobId;
          }
        } catch (parseError) {
          // Non-JSON response (e.g. "Accepted") — fall through and use clientJobId
          if (parseError instanceof SyntaxError) {
            console.log('Webhook returned non-JSON response, using client-generated jobId');
          } else {
            throw parseError;
          }
        }

        // Store jobId for polling and navigate to processing page
        localStorage.setItem(STORAGE_KEYS.currentJobId, jobId);
        localStorage.removeItem(STORAGE_KEYS.webinarResults);
        clearSavedData();
        navigate('/processing');
        return;
      } catch (error) {
        console.error('Webhook submission error:', error);
        setSubmitError(
          error instanceof Error
            ? error.message
            : 'Something went wrong. Please try again.'
        );
        setIsSubmitting(false);
        return;
      }
    }

    // Fallback when webhook is disabled: navigate to processing
    await new Promise((resolve) => setTimeout(resolve, 1500));
    clearSavedData();
    navigate('/processing', { state: { data: submissionData } });
  };

  return (
    <FormProvider {...form}>
      <div className="min-h-screen p-4 md:p-8">
        <div className="w-full max-w-3xl mx-auto space-y-8">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center space-y-2"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 text-sm">
              <CheckCircle2 className="w-4 h-4" />
              <span>Ready to Submit</span>
            </div>
            <h1 className="text-3xl font-bold text-slate-100">
              Review Your Answers
            </h1>
            <p className="text-slate-400">
              Take a moment to review your responses before submitting
            </p>
          </motion.div>

          <div className="space-y-4">
            <ReviewSection
              questionNumber={1}
              title={questions[0].title}
              onEdit={() => handleEdit(1)}
            >
              <ReviewField
                label="The #1 Mistake"
                value={formData.question1.mistake}
              />
              <ReviewField
                label="The Simple Fix"
                value={formData.question1.fix}
              />
            </ReviewSection>

            <ReviewSection
              questionNumber={2}
              title={questions[1].title}
              onEdit={() => handleEdit(2)}
            >
              <ReviewField
                label="Their Thank You"
                value={formData.question2.vision}
              />
            </ReviewSection>

            <ReviewSection
              questionNumber={3}
              title={questions[2].title}
              onEdit={() => handleEdit(3)}
            >
              <div className="space-y-3">
                {formData.question3.objections.map((objection, index) => (
                  <ReviewObjection
                    key={index}
                    index={index}
                    politeReason={objection.politeReason}
                    realReason={objection.realReason}
                  />
                ))}
              </div>
            </ReviewSection>

            <ReviewSection
              questionNumber={4}
              title={questions[3].title}
              onEdit={() => handleEdit(4)}
            >
              <ReviewField
                label="Why Your Solution"
                value={formData.question4.solution}
              />
              <ReviewField
                label="The Defining Moment"
                value={formData.question4.originStory}
              />
            </ReviewSection>

            <ReviewSection
              questionNumber={5}
              title={questions[4].title}
              onEdit={() => handleEdit(5)}
            >
              <ReviewList
                label="Free Resources"
                items={formData.question5.resources}
              />
            </ReviewSection>

            <ReviewSection
              questionNumber={6}
              title={questions[5].title}
              onEdit={() => handleEdit(6)}
            >
              <ReviewField
                label="Why You Say No"
                value={formData.question6.scarcity}
              />
              <ReviewField
                label="What They Missed"
                value={formData.question6.missedOpportunity}
              />
            </ReviewSection>
          </div>

          {validationErrors.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 rounded-xl bg-red-500/10 border border-red-500/20"
            >
              <h3 className="text-red-400 font-semibold mb-2">
                Please fix the following issues:
              </h3>
              <ul className="list-disc list-inside space-y-1">
                {validationErrors.map((error, idx) => (
                  <li key={idx} className="text-red-300 text-sm">
                    {error}
                  </li>
                ))}
              </ul>
            </motion.div>
          )}

          {submitError && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-start gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400"
            >
              <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="font-medium">Submission Failed</p>
                <p className="text-sm text-red-400/80">{submitError}</p>
              </div>
            </motion.div>
          )}

          <Card className="p-6">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <Button
                variant="secondary"
                onClick={() => navigate('/question/6')}
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Questions
              </Button>

              <Button
                size="lg"
                onClick={handleSubmit}
                isLoading={isSubmitting}
                className="w-full sm:w-auto"
              >
                {submitError ? 'Retry Submission' : 'Submit Questionnaire'}
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </Card>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-center text-sm text-slate-600"
          >
            By submitting, your answers will be processed to generate your
            custom webinar content.
          </motion.p>
        </div>
      </div>
    </FormProvider>
  );
}
